import { hexToRgb, getLuminance, safe } from '../utils/contrast.js';
import { store } from '../core/store.js';
import { getRenderLang, t } from '../utils/i18n.js';
import { escapeHtml, attrJson } from '../utils/dom.js';
import { basePublica } from '../utils/base-publica.js';
import { htmlCarta, presentacionCarta, vivoDePresentacion, opcionesDePresentacion } from '../utils/carta.js';

/**
 * MENU — Carta de restaurante con categorías y platos con precio.
 *
 * MODELO DE DATOS (anidado):
 *   props.categories: [{ name, items: [{ name, desc, price }] }]
 * A diferencia de features/process/bento (array PLANO `items`), aquí cada
 * categoría agrupa sus propios platos → estructura de DOS niveles. Por eso la
 * extracción NO es declarativa: usa un handler a medida en core/extractor.js
 * (CUSTOM_HANDLERS.menu) que recorre cada `.menu-category` y mapea sus
 * `.menu-dish` de vuelta a props.categories[c].items[i].
 *
 * TOOLBAR (add / borrar / mover): opera a nivel CATEGORÍA con
 * data-array="categories" / data-array-name="categories", reutilizando los
 * handlers globales (tools.js, dragdrop.js) que trabajan sobre arrays planos.
 * "Añadir" clona una categoría completa (con sus platos); "borrar"/"mover"
 * actúan sobre el grupo entero. Los platos se editan in-place vía data-field
 * (dish-name / dish-desc / dish-price) y llevan data-cat-index/data-item-index
 * para que el extractor a medida los reubique con precisión. Las categorías no
 * tienen icono, así que la toolbar omite el icon-picker.
 *
 * Variantes (CSS-driven → render único, DRY):
 *   v1 = Elegante: una columna centrada, categorías apiladas, líneas finas.
 *   v2 = Dos columnas: categorías repartidas en grid de 2 columnas.
 * En ambas, cada plato es una fila "nombre …… precio" con líder de puntos y la
 * descripción en pequeño debajo (estética editorial/restaurante).
 *
 * MODO ERP (T1b, `props.source.mode === 'erp'`): la carta la escribe el ERP, no el cliente.
 *   props.source = {mode:'erp', restaurant_id, ocultar_precios, secciones:'todas'|[ids], local,
 *                   estado:'ok'|'apagada'|'inexistente'|'error'|'' (pendiente), actualizada,
 *                   y la PRESENTACIÓN de «Personalizar la carta» (`utils/carta.js::presentacionCarta`):
 *                   alergenos, vista, max_platos, menu_pasos, ocultos, destacados}
 *   props.categories     = la FOTO de la carta (forma de siempre + campos del ERP; utils/carta.js)
 *   props.carta_alergenos = el catálogo de los alérgenos que salen en la foto
 * Lo pinta `utils/carta.js::htmlCarta` (el MISMO pintor que el runtime vivo) sin `data-field`, sin
 * `contenteditable` y sin botones: título y subtítulo siguen siendo del cliente (editables), los
 * platos no. Los parámetros del refresco vivo van en `data-carta-live` del HIJO `.menu-categories`,
 * no en el `<section>`: nada se inyecta en la sección, así que render.js y compileSiteHtml no
 * tienen nada que replicar (regla 2 del CLAUDE.md del front). El sello `.editor-only` cuenta el
 * estado y lleva «Actualizar» (lo atiende `editor/carta-erp.js`); compileSiteHtml lo borra.
 */

// Apertura de la sección: la MISMA en el modo manual y en el ERP (un solo sitio que la escribe).
const abreSeccion = (p, index, variant) => `<section id="bloque-menu-${index}" class="reveal menu-section variant-${variant}" style="--bg:${p.bgColor}; --text:${p.textColor}; --accent:${p.accentColor}; --font:'${p.fontFamily || "Inter"}'; padding: 100px 8%;">`;

// Encabezado compartido: título y subtítulo son del CLIENTE también en el modo ERP.
const cabecera = (p) => `
        <div class="menu-head">
            ${p.title ? `<h2 data-field="title" contenteditable="true">${p.title}</h2>` : ''}
            ${p.subtitle ? `<p data-field="subtitle" contenteditable="true">${p.subtitle}</p>` : ''}
        </div>`;

// E6 · un `menu` escrito a mano en un tenant con la carta pública ENCENDIDA: el editor lo avisa y
// ofrece conectarlo (`editor/carta-erp.js`, `.carta-erp-conectar`). `window.NUBE3I_CARTA` solo existe
// en el EDITOR (ver_sitio.html); `.editor-only` no llega al publicado. Va pegado a la apertura de la
// sección, sin blancos alrededor: quitándolo, el HTML es byte a byte el de siempre.
function avisoCartaManual() {
    const c = typeof window !== 'undefined' ? window.NUBE3I_CARTA : null;
    if (!c || !(c.encendidas > 0)) return '';
    return '<div class="carta-erp-aviso editor-only">'
        + '<span>Esta carta está escrita a mano y no se actualiza sola. Tienes la carta de tu local en el ERP.</span>'
        + '<button type="button" class="carta-erp-conectar">Conectar con tu carta</button></div>';
}

function _fechaCorta(iso) {
    if (typeof iso !== 'string' || !iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const dos = (n) => (n < 10 ? '0' : '') + n;
    return dos(d.getDate()) + '/' + dos(d.getMonth() + 1) + ' ' + dos(d.getHours()) + ':' + dos(d.getMinutes());
}

// Sello del editor (D-6): qué enseña este bloque y desde cuándo. Chrome del EDITOR en español
// (escapado); botones y nunca <a>: el editor intercepta todo <a> de #resultado en modo edición.
// En la SPA pública en vivo (`window.IS_PUBLIC`: el borrador que ve el VISITANTE) NO se emite: esa
// superficie arrancaba con `editorMode` hasta el 01/10/2026 (script.js lo fijaba sin mirar la bandera;
// hoy `store.js::edicionInicial()` lo apaga y esto queda como defensa en profundidad), así que
// `.editor-only` se vería y sus botones no tienen API a la que llamar (`editor/carta-erp.js::enElEditor`).
// Publicar no cambia: `compileSiteHtml` corre en el editor (sin esa bandera) y borra `.editor-only`.
function selloCartaErp(p, rid) {
    if (typeof window !== 'undefined' && window.IS_PUBLIC) return '';
    const src = p.source || {};
    const local = typeof src.local === 'string' ? src.local : '';
    let texto = '';
    let nota = '';
    if (src.estado === 'ok') {
        let platos = 0;
        let sinFoto = 0;
        (Array.isArray(p.categories) ? p.categories : []).forEach((cat) => {
            (cat && Array.isArray(cat.items) ? cat.items : []).forEach((it) => {
                platos++;
                if (!(it && it.foto)) sinFoto++;
            });
        });
        const fecha = _fechaCorta(src.actualizada);
        texto = ['Carta del ERP', local, platos + (platos === 1 ? ' plato' : ' platos'), sinFoto + ' sin foto',
                 fecha ? 'actualizada ' + fecha : ''].filter(Boolean).join(' · ');
        nota = 'Lo que ven los buscadores es la carta del día en que publicas.';
    } else if (src.estado === 'apagada') {
        texto = 'La carta de ' + (local || 'este local') + ' está apagada: la web no enseña ningún plato. Enciéndela en el Panel del local.';
    } else if (src.estado === 'inexistente') {
        texto = 'Este bloque apunta a un local que no existe en esta empresa. Elige otro local.';
    } else if (src.estado === 'error') {
        texto = 'No se pudo leer la carta: se mantiene la última copia.';
    } else {
        texto = 'Cargando la carta…';
    }
    const panel = basePublica() + '/panel_local/?restaurant_id=' + rid + '#local';
    return '<div class="carta-erp-sello editor-only">'
        + '<span>' + escapeHtml(texto) + '</span>'
        + (src.estado === 'ok' ? '<button type="button" class="carta-erp-personalizar">Personalizar</button>' : '')
        + '<button type="button" class="carta-erp-actualizar">Actualizar</button>'
        + '<button type="button" class="carta-erp-panel" data-href="' + escapeHtml(panel) + '">Panel del local</button>'
        + (src.estado === 'inexistente' ? '<button type="button" class="carta-erp-elegir">Elegir local</button>' : '')
        + (nota ? '<small>' + escapeHtml(nota) + '</small>' : '')
        + '</div>';
}

// Rama ERP: la foto de la carta, con los parámetros del refresco VIVO en `.menu-categories`.
function menuErp(p, index) {
    const variant = p.variant || 'v1';
    const src = p.source || {};
    // El id del local, o '' si no lo parece: es lo único del bloque que acaba en una URL (la del
    // Panel en el sello, y `/site_carta/?r=` en el runtime, que sin id ni pregunta).
    const crudo = String(src.restaurant_id == null ? '' : src.restaurant_id);
    const rid = /^\d{1,10}$/.test(crudo) ? crudo : '';
    const ocultar = src.ocultar_precios === true;
    const prefijo = 'mc' + index;   // ids del sprite: únicos aunque haya dos cartas en la página
    // Presentación y sus dos frases, ya en el idioma de la página: viajan horneadas en el atributo
    // (el publicado no carga `UI_LABELS`) y el runtime vivo repinta con las mismas.
    const pres = presentacionCarta(src);
    const textos = { aviso: t('cw_aviso_alergenos'), mas: t('cw_ver_todos') };
    const vivo = Object.assign({
        r: rid,
        l: getRenderLang(),           // compileSiteHtml lo fija por página ANTES del renderer
        p: ocultar ? 1 : 0,
        s: Array.isArray(src.secciones) ? src.secciones.map(String) : 'todas',
        x: prefijo,
    }, vivoDePresentacion(pres, textos));
    const interior = htmlCarta(p.categories, p.carta_alergenos, Object.assign(
        { ocultarPrecios: ocultar, prefijo: prefijo }, opcionesDePresentacion(pres, textos)));
    return `
    ${abreSeccion(p, index, variant)}${selloCartaErp(p, rid)}
        ${cabecera(p)}
        <div class="menu-categories" data-carta-live="${attrJson(vivo)}">${interior}</div>
    </section>`;
}

export const menuBlock = (rawProps, index = 0) => {
    const p = safe(rawProps);
    if (p.source && p.source.mode === 'erp') return menuErp(p, index);
    // safe() no normaliza `categories` (no es uno de los arrays conocidos) → guardia.
    const categories = Array.isArray(p.categories) ? p.categories : [];
    const variant = p.variant || 'v1';

    const isDark = getLuminance(...hexToRgb(p.bgColor)) < 0.179;
    const btnBg = isDark ? "#ffffff" : "#111827";
    const btnIcon = isDark ? "#111827" : "#ffffff";

    // --- Toolbar por categoría (sin icon-picker: las categorías no llevan icono) ---
    const catToolbar = (c) => `
        ${c !== 0 ? `<button class="item-add-btn card-add-left" data-array="categories" data-index="${c}">+</button>` : ''}
        <button class="item-add-btn card-add-right" data-array="categories" data-index="${c}">+</button>
        <div class="item-tools">
            <button class="tool-btn item-drag-handle" title="Mover" style="background: ${btnBg} !important; color: ${btnIcon} !important;">${window.icons.drag}</button>
            <button class="tool-btn item-delete-btn" data-array="categories" data-index="${c}" title="Borrar" style="background: ${btnBg} !important; color: ${btnIcon} !important;">${window.icons.trash}</button>
        </div>`;

    // --- Encabezado compartido ---
    const header = cabecera(p);

    // --- Categorías + platos (mismo HTML en v1/v2; el layout lo decide el CSS) ---
    const categoriesHtml = categories.map((cat, c) => {
        const dishes = Array.isArray(cat.items) ? cat.items : [];
        const dishesHtml = dishes.map((dish, i) => `
            <div class="menu-dish stagger-item" data-cat-index="${c}" data-item-index="${i}" style="--stagger-i:${i};">
                <button class="menu-del-dish editor-only" data-cat-index="${c}" data-item-index="${i}" title="Borrar plato" aria-label="Borrar plato">${window.icons.trash}</button>
                <div class="menu-dish-head">
                    <span class="dish-name" data-field="dish-name" data-cat-index="${c}" data-item-index="${i}" contenteditable="true">${dish.name || ''}</span>
                    <span class="menu-leader" aria-hidden="true"></span>
                    <span class="dish-price" data-field="dish-price" data-cat-index="${c}" data-item-index="${i}" contenteditable="true">${dish.price || ''}</span>
                </div>
                <p class="dish-desc" data-field="dish-desc" data-cat-index="${c}" data-item-index="${i}" contenteditable="true">${dish.desc || ''}</p>
            </div>`).join('');

        return `
        <div class="menu-category sortable-item stagger-item" data-item-index="${c}" data-array-name="categories" data-array="categories" data-index="${c}" style="--stagger-i:${c};">
            ${catToolbar(c)}
            <h3 class="menu-cat-name" data-field="cat-name" contenteditable="true">${cat.name || ''}</h3>
            <div class="menu-items">
                ${dishesHtml}
                <button class="menu-add-dish editor-only" data-cat-index="${c}" type="button" contenteditable="false">+ Añadir plato</button>
            </div>
        </div>`;
    }).join('');

    return `
    ${abreSeccion(p, index, variant)}${avisoCartaManual()}
        ${header}
        <div class="menu-categories">
            ${categoriesHtml}
        </div>
    </section>`;
};

// ───────────────────────────────────────────────────────────────
//  EDICIÓN DE PLATOS (modelo anidado): el "+ Añadir plato" y el borrar plato
//  no los cubren los handlers globales (operan sobre arrays planos). Aquí, un
//  único listener (editor) muta props.categories[c].items y re-renderiza.
//  El "+" de CATEGORÍA lo siguen gestionando los handlers globales (card-add-*).
// ───────────────────────────────────────────────────────────────
(function installMenuEditHandlers() {
    if (typeof window === 'undefined' || window.__menuEditInstalled) return;
    window.__menuEditInstalled = true;

    document.addEventListener('click', (e) => {
        const addBtn = e.target.closest('.menu-add-dish');
        const delBtn = e.target.closest('.menu-del-dish');
        if (!addBtn && !delBtn) return;
        if (!store.get('editorMode')) return;

        const wrapper = (addBtn || delBtn).closest('.block-editor-wrapper');
        if (!wrapper) return;
        const blockIndex = parseInt(wrapper.getAttribute('data-index'), 10);

        // Sincronizar las ediciones inline del DOM al store ANTES de mutar.
        store.set('siteBlocks', window.extraerDatosEditados(store.get('siteBlocks')));
        const blocks = store.get('siteBlocks');
        const block = blocks[blockIndex];
        if (!block || block.type !== 'menu' || !Array.isArray(block.props.categories)) return;

        if (addBtn) {
            const c = parseInt(addBtn.getAttribute('data-cat-index'), 10);
            const cat = block.props.categories[c];
            if (cat) {
                if (!Array.isArray(cat.items)) cat.items = [];
                cat.items.push({ name: 'Nuevo plato', desc: '', price: '' });
            }
        } else {
            const c = parseInt(delBtn.getAttribute('data-cat-index'), 10);
            const i = parseInt(delBtn.getAttribute('data-item-index'), 10);
            const cat = block.props.categories[c];
            if (cat && Array.isArray(cat.items)) cat.items.splice(i, 1);
        }

        window.renderPage(blocks);
        if (window.saveSnapshot) window.saveSnapshot(store.get('siteBlocks'));
    });
})();
